import { Add, Close, Delete, Edit, History, LinkTwoTone, MoreVert, Settings, Tune } from "@mui/icons-material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Card, Dialog, DialogActions, DialogContent, DialogTitle, FormLabel, Menu } from "@mui/material";
import { Autocomplete, Button, Grid, IconButton, MenuItem, Stack } from "@mui/material";
import { TextField, Typography } from "@mui/material";
import React, { MutableRefObject, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Recon360Context } from "src/Components/Recon360/Recon360";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import styled from "styled-components";
import {
  amountEntryType,
  ColumnsToSanitize,
  Group,
  LedgerConfigTemplate,
  LedgerConfiguration,
  ListReconSummaryAccountingSettingResponse,
  ListSanitizationRulesResponse,
} from "../../entity/recon-entity/ReconInterfaces";
import FormatDate from "../../Utils/DateUtils";
import { CustomButton } from "../Common/Button/CustomButton";
import LoadingIcon from "../Common/LoadingIcon";
import { DIALOG_MAX_WIDTHS } from "../Common/Modal/ModalWidth";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import "./ReconHome.scss";
import ColumnHeaderConfig, { rule } from "./Transformer/ColumnHeaderConfig";
import DocumentTypeConfig from "./Transformer/DocumentTypeConfig";

type StateDispatch<T> = React.Dispatch<React.SetStateAction<T>>;

export type Template = {
  templateId: number;
  templateName: string;
  updatedAt?: Date | string;
};

export interface ConfigTemplateNames {
  templates: Template[];
  defaultTemplateId: number;
}

export interface ConfigTemplateRes {
  template: {
    templateId: number;
    templateName: string;
    config: LedgerConfiguration;
    columnsToSanitize?: ColumnsToSanitize[];
    groups: Group[];
  };
}

const StyledComponent = styled.div`
  width: 100%;

  .table {
    border-collapse: collapse;
    table-layout: fixed;

    thead tr {
      background-color: #531c4c;
      user-select: none;
    }
    tbody tr {
      display: flex;
      align-items: center;
      min-height: 32px;
      font-family: "WorkSans";

      td {
        display: inline-block;
        font-size: 13px
        height: 32px;
      }
    }
    tr:last-child {
      border-bottom: 1px solid #dbdbdb;
    }

    th {
      font-size: 13px;
      color: white;
    }
    .th-bg {
      background-color: #391033;
    }
  }
`;

const TopHeader = ({
  headerText,
  icon,
  btnOnClick,
  pointer,
}: {
  headerText: string;
  icon: JSX.Element;
  btnOnClick?: () => void;
  pointer?: boolean;
}) => {
  return (
    <Box className="cardHeader space_between" gap={1.5}>
      <Typography fontSize={18} component={"span"}>
        {headerText}
      </Typography>
      <IconButton
        sx={{ ml: "auto", background: "rgba(160, 160, 210, 0.1)", cursor: pointer ? "pointer" : "default" }}
        onClick={btnOnClick}
        size="small"
      >
        {icon}
      </IconButton>
    </Box>
  );
};

const InformationCard = ({
  content,
  header,
  alternate,
  sx,
}: {
  content: React.ReactNode;
  header: React.ReactNode;
  alternate?: boolean;
  sx?: any;
}) => (
  <Box className={`informationCard fade_in_t ${alternate ? "alternate" : ""}`} style={sx}>
    <Box className="topContent">{content}</Box>
    <Box className="header">{header}</Box>
  </Box>
);

const ReconHome = ({ selectPartner }: { selectPartner: (partnerName: string) => void }) => {
  const { actor } = useContext(userContext);
  const { bpSelectedOptionRef, businessPartnerSelect } = useContext(Recon360Context);

  const { configListOwn, configList, defTemplateId, setDefTemplateId, ListAllConfigTemplateNamesRu } =
    useContext(Recon360Context);
  const [lastRecons, setLastRecons] = useState<{ timestamp: string; businessPartner: string }[]>([]);
  const [toleranceAmount, setToleranceAmount] = useState(0);
  const [outOfPeriod, setOutOfPeriod] = useState(false);
  const [amountEntryTerm, setAmountEntryTerm] = useState<"+ve/-ve" | "Dr/Cr">("+ve/-ve");

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  // Edit Template States
  const [templateSelectedId, setTemplateSelectedId] = useState("add");
  const [openEditConfig, setOpenEditConfig] = useState(false);
  const [configTemplate, setConfigTemplate] = useState<LedgerConfiguration>(null);
  const [templateName, setTemplateName] = useState("");
  const [sanitizeColumns, setSanitizeColumns] = useState<ColumnsToSanitize[]>([]);
  const groupsListRef = useRef<Group[]>([]);
  // End of Edit Template States

  const [openDeleteConfig, setOpenDeleteConfig] = useState(false);

  const handleOptionsClick = (event: React.MouseEvent<HTMLElement>, selectedTemplateId: string, _name: string) => {
    setTemplateSelectedId(selectedTemplateId);
    setTemplateName(_name);
    setAnchorEl(event.currentTarget);
  };
  const handleAdd = () => {
    setTemplateSelectedId("add");
    setConfigTemplate({} as LedgerConfiguration);
    setTemplateName("");
    setOpenEditConfig(true);
  };
  const handleEditMenuClose = () => {
    ListConfigTemplate(templateSelectedId);
    setAnchorEl(null);
  };
  const handleDeleteClose = () => {
    setOpenDeleteConfig(true);
    setAnchorEl(null);
  };

  const ListReconSummaryAccountingSetting = () => {
    useFetch<ListReconSummaryAccountingSettingResponse>(
      API_ENDPOINTS.LIST_RECON_SUMMARY_ACCOUNTING_SETTING.url,
      "GET",
      {
        failureMessage: API_ENDPOINTS.LIST_RECON_SUMMARY_ACCOUNTING_SETTING.failureMessage,
        thenCallBack: (response) => {
          setToleranceAmount(response.data.toleranceForAccounting);
          setOutOfPeriod(response.data.outOfPeriod);
          const termType = response.data.amountEntryType === amountEntryType.debitCredit ? "Dr/Cr" : "+ve/-ve";
          setAmountEntryTerm(termType);
        },
      }
    );
  };

  const ListConfigTemplate = (_templateId: string) => {
    useFetch<ConfigTemplateRes>(API_ENDPOINTS.GET_RU_CONFIG_TEMPLATE.url, "GET", {
      failureMessage: API_ENDPOINTS.GET_RU_CONFIG_TEMPLATE.failureMessage,
      config: {
        params: {
          templateId: _templateId,
        },
      },
      thenCallBack: (response) => {
        if (response.data.template) {
          setConfigTemplate(response.data.template.config);
          setTemplateName(response.data.template.templateName);
          setSanitizeColumns(response.data.template.columnsToSanitize);
          if (response.data.template.groups?.length > 0) {
            groupsListRef.current = response.data.template.groups;
          } else {
            groupsListRef.current = [];
          }
          setOpenEditConfig(true);
        }
      },
    });
  };

  // const GetLastThreeRecons = () => {
  //   useFetch<{ reconRuns: { timestamp: string; businessPartner: string }[] }>("/api/GetLastThreeRecons", "GET", {
  //     thenCallBack: (response) => {
  //       if (response.data.reconRuns.length > 0) {
  //         setLastRecons(response.data.reconRuns);
  //       }
  //     },
  //   });
  // };

  const InsertDefaultTemplate = (defTemplate: number) => {
    if (!defTemplate) return;
    // eslint-disable-next-line
    useFetch<{}>(API_ENDPOINTS.INSERT_DEFAULT_TEMPLATE.url, "POST", {
      failureMessage: API_ENDPOINTS.INSERT_DEFAULT_TEMPLATE.failureMessage,
      showSuccessToast: true,
      data: {
        templateId: defTemplate,
      },
      thenCallBack: (_res) => {
        const lastSelectedBp = bpSelectedOptionRef.current;
        if (lastSelectedBp) businessPartnerSelect({}, null);
        setTimeout(() => {
          if (lastSelectedBp) businessPartnerSelect({}, lastSelectedBp);
        }, 500);
      },
    });
  };

  useEffect(() => {
    ListReconSummaryAccountingSetting();
    ListAllConfigTemplateNamesRu();
    // GetLastThreeRecons();
    setLastRecons([]); // Temporarily disabled
  }, []);

  const accountName = actor.name.toLowerCase().replace(/[^a-z\d]+/gi, "");

  return (
    <Box display="flex" flexDirection="column" gap={2} p={3} pb={0}>
      <Grid className="containerGrid fade_up_t" gap={1}>
        {/* Left */}
        <Box id="configBox" className="fade_up_t d_flex" gap={1} sx={{ flexFlow: "column" }}>
          <Card id="templateCard" className="vertical_center_align" sx={{ gap: 2, px: 2, py: 2.5 }}>
            <TopHeader headerText="Configuration Templates" icon={<Tune />} />
            <Autocomplete
              multiple={false}
              className="w_250 fade_up_t"
              size="small"
              onChange={(_e, v) => {
                setDefTemplateId((v as Template).templateId);
                InsertDefaultTemplate((v as Template).templateId);
              }}
              id="defaultConfig"
              value={configList.find((_c) => _c.templateId === defTemplateId) || { templateName: " " }}
              options={configList}
              getOptionLabel={(op) => (op as Template).templateName}
              disableClearable={true}
              groupBy={(option) => option[0]}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Default Config"
                  label="Default Config"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>,
                  }}
                />
              )}
              sx={{
                minWidth: 300,
                opacity: 0,
              }}
            />
            <div className="blankBox" style={{ fontSize: 2 }}>
              &nbsp;
            </div>
            <TopHeader headerText="Saved Templates" icon={<Add />} btnOnClick={() => handleAdd()} pointer={true} />
            <StyledComponent>
              <table className="d_flex table sticky" style={{ flexFlow: "column", maxHeight: 200, overflow: "hidden" }}>
                <thead>
                  <tr className="d_flex">
                    <th style={{ flexGrow: 1 }}>Template Name</th>
                    <th style={{ marginLeft: "auto", width: 160 }}>Last Edited</th>
                    <th style={{ width: 100 }}> </th>
                  </tr>
                </thead>
                <tbody className="d_flex" style={{ flexFlow: "column", maxHeight: 200, overflowY: "auto" }}>
                  {/* only Ru template */}
                  {configList.map((_c, _i) => (
                    <tr key={_c.templateName + _c.templateId + _i}>
                      <td className="textOverflow_hidden" style={{ flexGrow: 1, width: "unset" }}>
                        {_c.templateName}
                      </td>
                      <td className="textOverflow_hidden" style={{ marginLeft: "auto", width: 160 }}>
                        {FormatDate(_c.updatedAt, "hh:mm A, DD MMM YY").toString()}
                      </td>
                      <td className="textOverflow_hidden" style={{ width: 100, paddingBlock: 1.5, paddingRight: 0 }}>
                        <Button
                          size="small"
                          endIcon={<MoreVert />}
                          sx={{ fontSize: 11 }}
                          onClick={(e) => handleOptionsClick(e, _c.templateId.toString(), _c.templateName)}
                        >
                          Options
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Menu
                anchorEl={anchorEl}
                open={openMenu}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  horizontal: "right",
                  vertical: "bottom",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem onClick={handleEditMenuClose} sx={{ fontSize: 13 }}>
                  <Edit className="mr_10" fontSize="small" /> Edit
                </MenuItem>
                <MenuItem onClick={handleDeleteClose} sx={{ fontSize: 13, color: "red" }}>
                  <Delete className="mr_10" fontSize="small" /> Delete
                </MenuItem>
              </Menu>
            </StyledComponent>
          </Card>
        </Box>
        {/* Top */}
        <Card id="quickLinks" className="fade_up_t vertical_center_align" sx={{ gap: 2, px: 2, py: 2.5 }}>
          <TopHeader headerText="Quick Link" icon={<LinkTwoTone sx={{ rotate: "-45deg" }} />} />
          <Box className="linkSegment fade_up_t">
            <Box className="circle">
              <i className="fa-solid fa-user-group" />
            </Box>
            <Box className="right">
              <Link to={`/${accountName}/partnerMaster`}>Partner Master</Link>
            </Box>
          </Box>
        </Card>
        {/* Right */}
        <Card id="information" className="fade_up_t vertical_center_align" sx={{ gap: 2, px: 2, py: 2.5 }}>
          <TopHeader headerText="Recon Configurations" icon={<Settings />} />
          <Stack direction={"row"} gap={1.5} flexWrap={"wrap"}>
            <InformationCard content={toleranceAmount} header="Tolerance" sx={{ "--delay": `1s` }} />
            <InformationCard content={outOfPeriod ? "Yes" : "No"} header="Out of Period" sx={{ "--delay": `0.75s` }} />
            <InformationCard content={amountEntryTerm} header="Terminology" sx={{ "--delay": `0.5s` }} />
          </Stack>
        </Card>
        {/* Bottom */}
        <Card id="lastRecons" className="fade_up_t vertical_center_align" sx={{ gap: 2, px: 2, py: 2.5 }}>
          <TopHeader headerText="Last 3 Reconciliations" icon={<History />} />
          <StyledComponent>
            <table className="d_flex table sticky" style={{ flexFlow: "column", maxHeight: 150, overflow: "hidden" }}>
              <thead>
                <tr className="d_flex">
                  <th style={{ flexGrow: 1 }}>Partner Name</th>
                  <th style={{ marginLeft: "auto", width: 200 }}>Last Reconciliation</th>
                </tr>
              </thead>
              <tbody className="d_flex" style={{ flexFlow: "column", maxHeight: 250, overflowY: "auto" }}>
                {lastRecons.map((rec, _i) => (
                  <tr key={rec.timestamp + rec.businessPartner + _i}>
                    <td
                      className="textOverflow_hidden_anchor"
                      style={{ flexGrow: 1, width: "unset", cursor: "pointer" }}
                      onClick={selectPartner.bind(null, rec.businessPartner)}
                    >
                      {rec.businessPartner}
                    </td>
                    <td className="textOverflow_hidden" style={{ marginLeft: "auto", width: 200 }}>
                      {FormatDate(rec.timestamp, "hh:mm A, DD MMM YY").toString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledComponent>
        </Card>
      </Grid>

      <ConfigEdit
        open={openEditConfig}
        setOpen={setOpenEditConfig}
        configListOwn={configListOwn}
        templateConfig={configTemplate}
        templateId={templateSelectedId}
        templateName={templateName}
        setTemplateName={setTemplateName}
        newTemplate={templateSelectedId === "add"}
        sanitizeColumns={sanitizeColumns}
        afterInsert={ListAllConfigTemplateNamesRu}
        afterUpdate={ListAllConfigTemplateNamesRu}
        groupsListRef={groupsListRef}
      />

      <DeleteTemplatesDialog
        open={openDeleteConfig}
        setOpen={setOpenDeleteConfig}
        templateSelected={templateSelectedId}
        templateName={templateName}
        defaultTemplateId={defTemplateId}
        afterClose={ListAllConfigTemplateNamesRu}
      />
    </Box>
  );
};

interface ConfigEditProps {
  open: boolean;
  setOpen: StateDispatch<boolean>;
  configListOwn: LedgerConfigTemplate[];
  templateId: string;
  templateName: string;
  setTemplateName?: StateDispatch<string>;
  newTemplate: boolean;
  templateConfig: LedgerConfiguration;
  sanitizeColumns: ColumnsToSanitize[];
  afterInsert?: () => void;
  afterUpdate?: () => void;
  groupsListRef: MutableRefObject<Group[]>;
}

const ConfigEdit = ({
  open,
  setOpen,
  configListOwn,
  templateConfig,
  templateId,
  templateName,
  setTemplateName,
  newTemplate,
  sanitizeColumns,
  afterInsert,
  afterUpdate,
  groupsListRef,
}: ConfigEditProps) => {
  const [columnHeaderConfigDialog, setColumnHeaderConfigDialog] = useState<boolean>(true);
  const [debitAmountSignReverseFlag, setDebitAmountSignReverseFlag] = useState<boolean>(false);
  const [creditAmountSignReverseFlag, setCreditAmountSignReverseFlag] = useState<boolean>(false);
  const [taxAmountSignReverseFlag, setTaxAmountSignReverseFlag] = useState<boolean>(false);
  const [updateConfig, setUpdateConfig] = useState(true); // when true edits, false to add
  const [specificConfig, setSpecificConfig] = useState(false);
  const [configTemplate, setConfigTemplate] = useState<LedgerConfiguration>(null);
  const [configTemplateUpdated, setConfigTemplateUpdated] = useState<LedgerConfiguration>(null);
  const [templateNameUpdated, setTemplateNameUpdated] = useState<string>("");
  const [sanitizeCols, setSanitizeCols] = useState<ColumnsToSanitize[]>([]);
  const [allRules, setAllRules] = useState<ColumnsToSanitize[]>([]);
  const [rulesArray, setRulesArray] = useState<rule[]>([]);

  // Recon360 Context
  const { getLedgerConfigList } = useContext(Recon360Context);

  useEffect(() => {
    setConfigTemplate(templateConfig);
    setDebitAmountSignReverseFlag(Boolean(templateConfig?.debitAmountSignReverseFlag));
    setCreditAmountSignReverseFlag(Boolean(templateConfig?.creditAmountSignReverseFlag));
    setTaxAmountSignReverseFlag(Boolean(templateConfig?.taxAmountSignReverseFlag));
    setUpdateConfig(!newTemplate);
    setSanitizeCols(sanitizeColumns);
    if (newTemplate) {
      setConfigTemplate(null);
      setConfigTemplateUpdated(null);
      setTemplateNameUpdated("");
    }
    ListSanitizeRule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateConfig, newTemplate, sanitizeColumns]);

  const ListSanitizeRule = () => {
    useFetch<ListSanitizationRulesResponse>(API_ENDPOINTS.LIST_SANITIZE_RULE.url, "GET", {
      failureMessage: API_ENDPOINTS.LIST_SANITIZE_RULE.failureMessage,
      thenCallBack: (response) => {
        if (response.data.sanitizationRules) {
          setRulesArray(response.data.sanitizationRules);
        }
      },
    });
  };

  const InsertNewConfig = (configData: LedgerConfiguration, _templateName: string, _rules: ColumnsToSanitize[]) => {
    Object.keys(configData).map((k) => {
      if (k === "debitAmountSignReverseFlag") {
        configData.debitAmountSignReverseFlag = debitAmountSignReverseFlag;
      } else if (k === "creditAmountSignReverseFlag") {
        configData.creditAmountSignReverseFlag = creditAmountSignReverseFlag;
      } else if (k === "taxAmountSignReverseFlag") {
        configData.taxAmountSignReverseFlag = taxAmountSignReverseFlag;
      }
      return configData;
    });
    if (!Object.prototype.hasOwnProperty.call(configData, "debitAmountSignReverseFlag")) {
      configData.debitAmountSignReverseFlag = debitAmountSignReverseFlag;
    }
    if (!Object.prototype.hasOwnProperty.call(configData, "creditAmountSignReverseFlag")) {
      configData.creditAmountSignReverseFlag = creditAmountSignReverseFlag;
    }
    if (!Object.prototype.hasOwnProperty.call(configData, "taxAmountSignReverseFlag")) {
      configData.taxAmountSignReverseFlag = taxAmountSignReverseFlag;
    }
    useFetch<ConfigTemplateRes>(API_ENDPOINTS.ADD_RU_CONFIG_TEMPLATE.url, "POST", {
      failureMessage: API_ENDPOINTS.ADD_RU_CONFIG_TEMPLATE.failureMessage,
      showSuccessToast: true,
      data: {
        templateName: _templateName,
        config: configData,
        columnsToSanitize: _rules,
        groups: groupsListRef.current,
      },
      thenCallBack: (response) => {
        if (response.data.config) {
          setConfigTemplate(response.data.config);
        }
        if (afterInsert) afterInsert();

        if (getLedgerConfigList) getLedgerConfigList();
      },
    });
  };

  const UpdateConfig = (configData: LedgerConfiguration, _templateName: string, _rules: ColumnsToSanitize[]) => {
    Object.keys(configData).map((k) => {
      if (k === "debitAmountSignReverseFlag") {
        configData.debitAmountSignReverseFlag = debitAmountSignReverseFlag;
      } else if (k === "creditAmountSignReverseFlag") {
        configData.creditAmountSignReverseFlag = creditAmountSignReverseFlag;
      } else if (k === "taxAmountSignReverseFlag") {
        configData.taxAmountSignReverseFlag = taxAmountSignReverseFlag;
      }
      return configData;
    });
    if (!Object.prototype.hasOwnProperty.call(configData, "debitAmountSignReverseFlag")) {
      configData.debitAmountSignReverseFlag = debitAmountSignReverseFlag;
    }
    if (!Object.prototype.hasOwnProperty.call(configData, "creditAmountSignReverseFlag")) {
      configData.creditAmountSignReverseFlag = creditAmountSignReverseFlag;
    }
    if (!Object.prototype.hasOwnProperty.call(configData, "taxAmountSignReverseFlag")) {
      configData.taxAmountSignReverseFlag = taxAmountSignReverseFlag;
    }

    useFetch(API_ENDPOINTS.UPDATE_RU_CONFIG_TEMPLATE.url, "PUT", {
      failureMessage: API_ENDPOINTS.UPDATE_RU_CONFIG_TEMPLATE.failureMessage,
      showSuccessToast: true,
      data: {
        templateName: _templateName,
        config: configData,
        columnsToSanitize: _rules,
        templateId,
        groups: groupsListRef.current,
      },
      thenCallBack: () => {
        if (afterUpdate) afterUpdate();

        if (getLedgerConfigList) getLedgerConfigList();
      },
    });
  };

  return (
    <Dialog open={open} maxWidth={DIALOG_MAX_WIDTHS.MD} fullWidth>
      {columnHeaderConfigDialog ? (
        <ColumnHeaderConfig
          name="Recon Home"
          setColumnHeaderConfigDialog={setColumnHeaderConfigDialog}
          headerName={updateConfig ? configTemplate : configTemplateUpdated || ({} as LedgerConfiguration)}
          setHeaderName={setConfigTemplate}
          rules={rulesArray}
          templateName={updateConfig ? templateName : templateNameUpdated || ""}
          setTemplateName={setTemplateName}
          sanitizeCol={sanitizeCols}
          template={true}
          newconfig={updateConfig}
          setNewMapping={setUpdateConfig}
          heading="Column header mapping"
          openNextDialog={(sanitizeCol: ColumnsToSanitize[], header: LedgerConfiguration, template: string) => {
            setAllRules(sanitizeCol);
            setSanitizeCols(sanitizeCol);
            setConfigTemplateUpdated(header);
            setTemplateNameUpdated(template);
            setColumnHeaderConfigDialog(false);
          }}
          showSettingsIcon={true}
          setOpenDialog={setOpen}
          specificConfig={specificConfig}
          setSpecificConfig={setSpecificConfig}
          disableSpecificConfigCheckbox={true}
          configList={configListOwn}
          debitAmountSignReverseFlag={debitAmountSignReverseFlag}
          setDebitAmountSignReverseFlag={setDebitAmountSignReverseFlag}
          creditAmountSignReverseFlag={creditAmountSignReverseFlag}
          setCreditAmountSignReverseFlag={setCreditAmountSignReverseFlag}
          taxAmountSignReverseFlag={taxAmountSignReverseFlag}
          setTaxAmountSignReverseFlag={setTaxAmountSignReverseFlag}
          subTab={"Ledger"}
          saveRulesApi={(header) => {
            if (!updateConfig)
              InsertNewConfig(header as LedgerConfiguration, templateNameUpdated || templateName, allRules);
            else UpdateConfig(header as LedgerConfiguration, templateNameUpdated || templateName, allRules);

            setOpen(false);
            setColumnHeaderConfigDialog(true);
          }}
          editingTemplate={true}
          companyId={null}
          branchCode={null}
          groupsListRef={groupsListRef}
        />
      ) : (
        <DocumentTypeConfig
          setColumnHeaderConfigDialog={setColumnHeaderConfigDialog}
          headerName={updateConfig ? configTemplate : configTemplateUpdated || ({} as LedgerConfiguration)}
          setHeaderName={setConfigTemplate}
          setNewMapping={setUpdateConfig}
          name="Configuration File"
          heading="Document Type Key"
          backTemplate={() => {
            setColumnHeaderConfigDialog(true);
          }}
          setOpenDialog={setOpen}
          saveRulesApi={(config: LedgerConfiguration) => {
            if (!updateConfig)
              InsertNewConfig(config as LedgerConfiguration, templateNameUpdated || templateName, allRules);
            else UpdateConfig(config as LedgerConfiguration, templateNameUpdated || templateName, allRules);

            setOpen(false);
            setColumnHeaderConfigDialog(true);
          }}
          setDebitAmountSignReverseFlag={setDebitAmountSignReverseFlag}
          setCreditAmountSignReverseFlag={setCreditAmountSignReverseFlag}
          setTaxAmountSignReverseFlag={setTaxAmountSignReverseFlag}
          editingTemplate={true}
          groupsListRef={groupsListRef}
        />
      )}
    </Dialog>
  );
};

interface DeleteTemplateProps {
  open: boolean;
  setOpen: StateDispatch<boolean>;
  templateSelected: string;
  templateName: string;
  defaultTemplateId: number;
  afterClose?: () => Promise<void>;
}

const DeleteTemplatesDialog = ({
  open,
  setOpen,
  templateSelected,
  templateName,
  defaultTemplateId,
  afterClose,
}: DeleteTemplateProps) => {
  // Recon360 Context
  const { getLedgerConfigList, bpSelectedOptionRef, businessPartnerSelect } = useContext(Recon360Context);
  const [isDeletingTemplate, setIsDeletingTemplate] = useState(false);

  const DeleteTemplate = () => {
    setIsDeletingTemplate(true);
    useFetch(API_ENDPOINTS.DELETE_RU_CONFIG_TEMPLATE.url, "POST", {
      failureMessage: API_ENDPOINTS.DELETE_RU_CONFIG_TEMPLATE.failureMessage,
      showSuccessToast: true,
      data: {
        templateId: parseInt(templateSelected),
      },
      thenCallBack: async (_res) => {
        setIsDeletingTemplate(false);
        await afterClose();
        if (templateSelected?.toString() === defaultTemplateId?.toString()) {
          const lastSelectedBp = bpSelectedOptionRef.current;
          if (lastSelectedBp) businessPartnerSelect({}, null);
          setTimeout(() => {
            if (lastSelectedBp) businessPartnerSelect({}, lastSelectedBp);
          }, 500);
        }

        if (getLedgerConfigList) getLedgerConfigList();
      },
      catchCallBack: () => {
        setIsDeletingTemplate(false);
      },
    });
  };

  return (
    <Dialog open={open} maxWidth={DIALOG_MAX_WIDTHS.SM} fullWidth={true}>
      <DialogTitle className="dialog-title">
        <div className="heading-m error-dialog-icon">
          <div>
            <WarningAmberIcon color="error" />
          </div>
          Delete Config Template
        </div>
        <div className="vertical_center_align">
          <IconButton
            onClick={() => {
              setOpen(false);
            }}
          >
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexFlow: "column", gap: 0.5 }}>
        <FormLabel>Are you sure, You want to Delete Template?</FormLabel>
        <FormLabel>
          <b>Template:</b> {templateName}
        </FormLabel>
      </DialogContent>
      <DialogActions className="dialog-actions">
        <CustomButton
          size="medium"
          color="error"
          variant="text"
          onClick={() => {
            setOpen(false);
          }}
        >
          Cancel
        </CustomButton>
        <CustomButton
          disabled={isDeletingTemplate}
          startIcon={<LoadingIcon loading={isDeletingTemplate} />}
          onClick={() => {
            setOpen(false);
            DeleteTemplate();
          }}
          size="medium"
          color="error"
          variant="contained"
        >
          Delete
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ReconHome;
